import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LayoutGrid, Euro, MapPin, Wifi, Coffee, Snowflake } from "lucide-react";
import { Logo, Page } from "../components/Layout";
import { track } from "../api";

const Badge = ({ icon: Icon, children }) => (
  <div className="badge-chip inline-flex items-center gap-1.5 bg-white">
    <Icon size={14} strokeWidth={1.8} color="#7C1FD6" /> {children}
  </div>
);

const PriceCard = ({ title, price, premium, onClick }) => (
  <div className={`card p-5 flex-1 ${premium ? "ring-1 ring-violet-200" : ""}`}>
    {premium && <div className="text-[0.6rem] uppercase tracking-widest text-violet-700 font-semibold mb-1">Premium</div>}
    <div className="serif text-xl">{title}</div>
    <div className="mt-2 text-3xl font-bold">{price} €<span className="text-sm font-normal text-gray-400">/giorno</span></div>
    <button data-testid={`home-verify-${premium ? "cabina" : "openspace"}`} onClick={onClick}
      className="btn-outline w-full mt-4 text-sm">Verifica disponibilità</button>
  </div>
);

export default function HomePage() {
  const nav = useNavigate();
  useEffect(() => { track("home_view"); }, []);

  return (
    <Page>
      {/* Hero */}
      <section className="text-center pt-4">
        <Logo className="inline-block" />
        <div className="text-xs tracking-[0.28em] uppercase text-gray-500 mt-2">Tricopigmentazione · Coworking Studio</div>
        <p className="text-sm text-gray-600 mt-4">Coworking per artisti della pelle</p>
        <div className="inline-flex items-center gap-1.5 text-xs text-gray-500 mt-2">
          <MapPin size={13} /> Corso Francia 162 · Rivoli (TO)
        </div>

        <h1 className="serif text-4xl md:text-5xl leading-[1.05] tracking-tight mt-7">
          Il tuo studio.<br /><span className="text-violet-700">Quando ti serve.</span>
        </h1>
        <p className="text-gray-600 mt-4 max-w-md mx-auto">
          Prenota solo quando ti serve. Nessun affitto fisso.
        </p>

        <div className="flex flex-wrap justify-center gap-2 mt-6">
          <Badge icon={Euro}>Da 39 €/giorno</Badge>
          <Badge icon={Clock}>Accesso H24</Badge>
          <Badge icon={LayoutGrid}>Solo 3 postazioni</Badge>
        </div>

        <div className="flex flex-col gap-3 mt-8">
          <button data-testid="cta-coworking" onClick={() => nav("/prenota")} className="btn-primary w-full">
            Cerco una postazione
            <span className="block text-xs font-normal opacity-90">Prenotala! Senza affitto fisso.</span>
          </button>
          <button data-testid="cta-trico" onClick={() => nav("/tricopigmentazione")} className="btn-dark w-full">
            Tricopigmentazione
            <span className="block text-xs font-normal opacity-90">Scopri il trattamento MAS · Effetto rasato o densità</span>
          </button>
          <button data-testid="cta-academy" onClick={() => nav("/academy")} className="btn-primary w-full">
            MAS Academy
            <span className="block text-xs font-normal opacity-90">Corsi professionali multi-giornata · Docenti selezionati</span>
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="cta-vetrina" onClick={() => nav("/flash/led")} className="btn-outline">Messaggio in Vetrina</button>
            <button data-testid="cta-flashday" onClick={() => nav("/flash-day")} className="btn-outline">Flash Day</button>
            <button data-testid="cta-corsi" onClick={() => nav("/corsi")} className="btn-outline">Corsi & Eventi</button>
            <button data-testid="cta-invita" onClick={() => nav("/profilo")} className="btn-outline">Invita e ricevi premi</button>
          </div>
        </div>
      </section>

      {/* Boutique statement */}
      <section className="mt-12 bg-[#0f0f12] text-white rounded-2xl p-7 text-center">
        <div className="serif text-2xl">Solo 3 artisti contemporaneamente.</div>
        <p className="text-gray-300 text-sm mt-3">Più spazio. Più privacy. Più attenzione.<br />Un coworking boutique, non una sala affollata.</p>
      </section>

      {/* Pricing */}
      <section className="mt-12">
        <h2 className="serif text-2xl tracking-tight mb-4">Le postazioni</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <PriceCard title="Open Space" price={45} onClick={() => nav("/prenota")} />
          <PriceCard title="Cabina Privacy Premium" price={55} premium onClick={() => nav("/prenota")} />
        </div>
        <button data-testid="home-prenota" onClick={() => nav("/prenota")} className="btn-primary w-full mt-5">Verifica disponibilità</button>
      </section>

      {/* Services */}
      <section className="mt-12">
        <h2 className="serif text-2xl tracking-tight mb-4">Lo studio</h2>
        <div className="card p-5 grid grid-cols-2 gap-y-3 text-sm text-gray-700">
          {["2 Postazioni Open Space", "1 Cabina Privacy Premium", "Accesso H24", "Ambiente climatizzato", "Wi-Fi & Domotica", "Acqua, bibite, caffè inclusi", "Area consulenze"].map((s) => (
            <div key={s} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-violet-600" />{s}</div>
          ))}
        </div>
      </section>

      {/* Tricopigmentazione teaser */}
      <section className="mt-12">
        <h2 className="serif text-2xl tracking-tight mb-4">Tricopigmentazione</h2>
        <div className="card p-5">
          <p className="text-sm text-gray-600">Il trattamento che ridà densità e sicurezza, uomo e donna. Consulenza gratuita e valutazione con foto.</p>
          <div className="mt-3 text-sm"><span className="text-gray-400 line-through">1.250 €</span> <span className="serif text-2xl text-violet-700 ml-2">1.000 €</span></div>
          <button data-testid="home-trico" onClick={() => nav("/tricopigmentazione")} className="btn-primary w-full mt-4">Richiedi una consulenza</button>
        </div>
      </section>

      {/* Invita e ricevi premi */}
      <section className="mt-12">
        <div className="card p-5 flex items-center justify-between gap-3">
          <div>
            <h2 className="serif text-xl tracking-tight">Invita e ricevi premi</h2>
            <p className="text-sm text-gray-600 mt-1">Buono Amazon 100 € (Trico) o 20 € (Coworking).</p>
          </div>
          <button data-testid="home-invita" onClick={() => nav("/invita")} className="btn-primary shrink-0">Invita</button>
        </div>
      </section>

      <footer className="mt-14 text-center text-xs text-gray-400">
        <Logo className="inline-block scale-75" />
        <p className="mt-2">Corso Francia 162 · Rivoli (TO) · Italia</p>
        <p className="mt-1">MAS non sostituisce il tuo studio. Può diventare la tua seconda sede a Rivoli.</p>
      </footer>
    </Page>
  );
}
